'use client'

import { forwardRef, ReactNode, useState, useMemo, useEffect } from 'react'
import { ChevronUp, ChevronDown, Search, Filter, MoreHorizontal, AlertTriangle, Check, Loader2, Eye, EyeOff } from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentAnimations,
  componentShadows,
  componentGlass,
  typographyV3 
} from '@birhaus/tokens/v3'
import { cn, ClassValue } from '@birhaus/types/utils'

/**
 * BIRHAUS Table V3 - Radical Minimalist Data Display
 * 
 * Revolutionary table component with:
 * - Glass morphism headers with backdrop blur
 * - Smooth hover row effects and animations
 * - Radical border radius (20px default)
 * - Generous spacing (2x traditional)
 * - Enhanced sorting animations
 * - Miller's Law compliance (max 7 columns)
 * - Spanish-first labeling
 * - Performance optimized rendering
 */

// Using imported cn utility from @birhaus/types/utils

export interface BirhausTableV3Column<T = any> {
  id: string
  header: string
  headerEs?: string
  headerEn?: string
  accessor: keyof T | ((item: T) => any)
  
  // Column behavior
  sortable?: boolean
  filterable?: boolean
  searchable?: boolean
  resizable?: boolean
  
  // V3 Formatting enhancements
  cell?: (value: any, item: T, index: number) => ReactNode
  width?: string | number
  minWidth?: string | number
  maxWidth?: string | number
  align?: 'left' | 'center' | 'right'
  
  // Enhanced formatting
  isCurrency?: boolean
  isPercentage?: boolean
  isDate?: boolean
  isNumeric?: boolean
  
  // V3 Visual features
  sticky?: boolean
  glassMorphism?: boolean
  glow?: boolean
  
  // Progressive disclosure
  priority?: 'high' | 'medium' | 'low'
  hideOnMobile?: boolean
  hideOnTablet?: boolean
  
  // Cognitive load hints
  cognitiveWeight?: number // 1-10 scale
}

export interface BirhausTableV3Props<T = any> {
  // Data
  data: T[]
  columns: BirhausTableV3Column<T>[]
  
  // BIRHAUS Principle #7: Spanish-first labeling
  emptyMessage?: string
  emptyMessageEs?: string
  emptyMessageEn?: string
  loadingMessage?: string
  loadingMessageEs?: string
  loadingMessageEn?: string
  
  // V3 Design variants
  variant?: 'default' | 'glass' | 'frosted' | 'elevated' | 'minimal' | 'bordered'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  radius?: keyof typeof borderRadiusV3
  
  // V3 Visual effects
  glassMorphism?: boolean
  glow?: boolean
  float?: boolean
  hoverEffects?: boolean
  stripedRows?: boolean
  
  // Miller's Law compliance
  maxVisibleColumns?: number
  showCognitiveWarning?: boolean
  autoHideColumns?: boolean
  
  // Functionality
  sortable?: boolean
  filterable?: boolean
  searchable?: boolean
  selectable?: boolean
  multiSelect?: boolean
  
  // State management
  loading?: boolean
  selectedRows?: Set<string | number>
  onRowSelect?: (rowId: string | number, item: T) => void
  onRowsSelect?: (rowIds: Set<string | number>) => void
  
  // Sorting
  sortColumn?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (column: string, direction: 'asc' | 'desc') => void
  
  // Filtering
  filters?: Record<string, any>
  onFilter?: (filters: Record<string, any>) => void
  
  // Search
  searchValue?: string
  onSearch?: (value: string) => void
  
  // Pagination
  pagination?: {
    page: number
    pageSize: number
    total: number
    onPageChange: (page: number) => void
    onPageSizeChange: (pageSize: number) => void
  }
  
  // Row identification
  getRowId?: (item: T, index: number) => string | number
  
  // Row actions
  rowActions?: Array<{
    id: string
    label: string
    labelEs?: string
    labelEn?: string
    icon?: ReactNode
    onClick: (item: T) => void
    variant?: 'primary' | 'secondary' | 'destructive' | 'glass'
    disabled?: (item: T) => boolean
    hidden?: (item: T) => boolean
  }>
  
  // Interaction handlers
  onRowClick?: (item: T, index: number) => void
  onRowDoubleClick?: (item: T, index: number) => void
  onRowHover?: (item: T, index: number) => void
  
  // Performance
  virtualScrolling?: boolean
  trackPerformance?: boolean
  
  // Styling
  className?: string
  headerClassName?: string
  bodyClassName?: string
  rowClassName?: string | ((item: T, index: number) => string)
}

export const BirhausTableV3 = forwardRef<HTMLTableElement, BirhausTableV3Props>(({
  // Data
  data,
  columns,
  
  // Messages (Spanish-first)
  emptyMessage,
  emptyMessageEs,
  emptyMessageEn,
  loadingMessage,
  loadingMessageEs,
  loadingMessageEn,
  
  // V3 Design
  variant = 'glass',
  size = 'md',
  radius = 'lg',
  
  // V3 Effects
  glassMorphism = true,
  glow = false,
  float = true,
  hoverEffects = true,
  stripedRows = true,
  
  // Miller's Law settings
  maxVisibleColumns = 7,
  showCognitiveWarning = true,
  autoHideColumns = false,
  
  // Functionality
  sortable = true,
  filterable = false,
  searchable = false,
  selectable = false,
  multiSelect = false,
  
  // State
  loading = false,
  selectedRows = new Set(),
  onRowSelect,
  onRowsSelect,
  
  // Sorting
  sortColumn,
  sortDirection,
  onSort,
  
  // Filtering
  filters,
  onFilter,
  
  // Search
  searchValue,
  onSearch,
  
  // Pagination
  pagination,
  
  // Row identification
  getRowId,
  
  // Actions
  rowActions = [],
  
  // Handlers
  onRowClick,
  onRowDoubleClick,
  onRowHover,
  
  // Performance
  virtualScrolling = false,
  trackPerformance = false,
  
  // Styling
  className,
  headerClassName,
  bodyClassName,
  rowClassName,
}, ref) => {
  
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set())
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)
  const [animatingSort, setAnimatingSort] = useState(false)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayEmptyMessage = resolveText(
    emptyMessageEs,
    emptyMessageEn,
    emptyMessage || 'No hay datos disponibles'
  )
  
  const displayLoadingMessage = resolveText(
    loadingMessageEs,
    loadingMessageEn,
    loadingMessage || 'Cargando...'
  )
  
  // Miller's Law compliance - automatically manage column visibility
  const effectiveColumns = useMemo(() => {
    if (!autoHideColumns) return columns
    
    // Prioritize columns and limit to maxVisibleColumns
    const prioritized = [...columns].sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      const aPriority = priorityOrder[a.priority || 'medium']
      const bPriority = priorityOrder[b.priority || 'medium']
      
      if (aPriority !== bPriority) return aPriority - bPriority
      
      // Secondary sort by cognitive weight
      return (a.cognitiveWeight || 5) - (b.cognitiveWeight || 5)
    })
    
    return prioritized.slice(0, maxVisibleColumns)
  }, [columns, autoHideColumns, maxVisibleColumns])
  
  // Initialize visible columns
  useEffect(() => {
    if (visibleColumns.size === 0) {
      const initialVisible = new Set(effectiveColumns.slice(0, maxVisibleColumns).map(col => col.id))
      setVisibleColumns(initialVisible)
    }
  }, [effectiveColumns, maxVisibleColumns, visibleColumns.size])
  
  // V3 Size classes with generous spacing
  const sizeClasses = {
    xs: 'text-sm [&_td]:px-4 [&_td]:py-3 [&_th]:px-4 [&_th]:py-3',
    sm: 'text-sm [&_td]:px-6 [&_td]:py-4 [&_th]:px-6 [&_th]:py-4',
    md: 'text-base [&_td]:px-8 [&_td]:py-6 [&_th]:px-8 [&_th]:py-6',
    lg: 'text-lg [&_td]:px-10 [&_td]:py-8 [&_th]:px-10 [&_th]:py-8',
    xl: 'text-xl [&_td]:px-12 [&_td]:py-10 [&_th]:px-12 [&_th]:py-10',
  }
  
  // V3 Variant classes
  const variantClasses = {
    default: 'bg-white border border-gray-200',
    glass: `${componentGlass.table} backdrop-blur-lg border border-white/30`,
    frosted: `${glassV3.effects.heavy} backdrop-blur-xl border border-white/40`,
    elevated: 'bg-white shadow-elevation-3 border border-gray-100',
    minimal: 'bg-transparent',
    bordered: 'bg-white border-2 border-gray-300',
  }
  
  // Handle sorting
  const handleSort = (columnId: string) => {
    if (!sortable || !onSort) return
    
    setAnimatingSort(true)
    
    const newDirection = 
      sortColumn === columnId && sortDirection === 'asc' ? 'desc' : 'asc'
    
    onSort(columnId, newDirection)
    
    setTimeout(() => setAnimatingSort(false), 300)
  }
  
  // Handle row selection
  const handleRowSelect = (item: any, index: number) => {
    const rowId = getRowId ? getRowId(item, index) : index
    
    if (multiSelect) {
      const newSelection = new Set(selectedRows)
      if (newSelection.has(rowId)) {
        newSelection.delete(rowId)
      } else {
        newSelection.add(rowId)
      }
      onRowsSelect?.(newSelection)
    } else {
      onRowSelect?.(rowId, item)
    }
  }
  
  // Handle select all
  const handleSelectAll = () => {
    if (!multiSelect || !onRowsSelect) return
    
    const allRowIds = data.map((item, index) => 
      getRowId ? getRowId(item, index) : index
    )
    
    const isAllSelected = allRowIds.every(id => selectedRows.has(id))
    
    if (isAllSelected) {
      onRowsSelect(new Set())
    } else {
      onRowsSelect(new Set(allRowIds))
    }
  }
  
  // Column visibility toggle
  const toggleColumnVisibility = (columnId: string) => {
    const newVisible = new Set(visibleColumns)
    
    if (newVisible.has(columnId)) {
      // Don't allow hiding all columns
      if (newVisible.size > 1) {
        newVisible.delete(columnId)
      }
    } else {
      // Check Miller's Law compliance
      if (newVisible.size < maxVisibleColumns) {
        newVisible.add(columnId)
      }
    }
    
    setVisibleColumns(newVisible)
  }
  
  // Get cell value
  const getCellValue = (item: any, column: BirhausTableV3Column) => {
    if (typeof column.accessor === 'function') {
      return column.accessor(item)
    }
    return item[column.accessor]
  }
  
  // Format cell value
  const formatCellValue = (value: any, column: BirhausTableV3Column) => {
    if (column.isCurrency) {
      return new Intl.NumberFormat('es-PY', {
        style: 'currency',
        currency: 'PYG'
      }).format(value || 0)
    }
    
    if (column.isPercentage) {
      return `${((value || 0) * 100).toFixed(1)}%`
    }
    
    if (column.isDate && value) {
      return new Intl.DateTimeFormat('es-PY').format(new Date(value))
    }
    
    if (column.isNumeric) {
      return new Intl.NumberFormat('es-PY').format(value || 0)
    }
    
    return value
  }
  
  const isAllSelected = data.length > 0 && 
    data.every((item, index) => {
      const rowId = getRowId ? getRowId(item, index) : index
      return selectedRows.has(rowId)
    })
  
  const isIndeterminate = selectedRows.size > 0 && !isAllSelected
  
  if (loading) {
    return (
      <div className={cn(
        'flex items-center justify-center',
        'p-16 rounded-2xl',
        'bg-gray-50/50 backdrop-blur-sm',
        className
      )}>
        <div className="flex items-center gap-4">
          <Loader2 size={24} className="animate-spin text-blue-600" />
          <span className="text-gray-600 font-medium">{displayLoadingMessage}</span>
        </div>
      </div>
    )
  }
  
  if (!data || data.length === 0) {
    return (
      <div className={cn(
        'flex flex-col items-center justify-center',
        'p-16 rounded-2xl',
        'bg-gray-50/50 backdrop-blur-sm text-center',
        className
      )}>
        <AlertTriangle size={48} className="text-gray-400 mb-4" />
        <p className="text-gray-600 font-medium text-lg">{displayEmptyMessage}</p>
      </div>
    )
  }
  
  return (
    <div className={cn(
      'relative w-full',
      float && 'transform-gpu hover:scale-[1.01] transition-transform duration-300',
      className
    )}>
      {/* Column visibility controls */}
      {(effectiveColumns.length > maxVisibleColumns) && (
        <div className={cn(
          'flex items-center gap-4 mb-6 p-4 rounded-2xl',
          'bg-blue-50/80 border border-blue-200/50',
          glassMorphism && 'backdrop-blur-sm'
        )}>
          <Eye size={20} className="text-blue-600" />
          <span className="text-blue-900 font-medium text-sm">
            Columnas visibles ({visibleColumns.size}/{maxVisibleColumns}):
          </span>
          <div className="flex flex-wrap gap-2">
            {effectiveColumns.map(column => (
              <button
                key={column.id}
                onClick={() => toggleColumnVisibility(column.id)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-medium',
                  'transition-all duration-200',
                  'border',
                  visibleColumns.has(column.id) ? [
                    'bg-blue-600 text-white border-blue-600',
                    'hover:bg-blue-700 hover:border-blue-700',
                  ] : [
                    'bg-white text-blue-600 border-blue-200',
                    'hover:bg-blue-50 hover:border-blue-300',
                  ]
                )}
              >
                {visibleColumns.has(column.id) ? (
                  <Eye size={12} className="inline mr-1" />
                ) : (
                  <EyeOff size={12} className="inline mr-1" />
                )}
                {column.headerEs || column.headerEn || column.header}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Cognitive load warning */}
      {showCognitiveWarning && visibleColumns.size > 5 && (
        <div className={cn(
          'flex items-center gap-3 mb-6 p-4 rounded-2xl',
          'bg-yellow-50/80 border border-yellow-200/50',
          glassMorphism && 'backdrop-blur-sm'
        )}>
          <AlertTriangle size={20} className="text-yellow-600" />
          <div className="flex-1">
            <p className="text-yellow-900 font-medium text-sm">
              Advertencia de carga cognitiva
            </p>
            <p className="text-yellow-700 text-sm">
              Mostrando {visibleColumns.size} columnas. Considera reducir a 5 o menos para mejor usabilidad.
            </p>
          </div>
        </div>
      )}
      
      {/* Table */}
      <div className={cn(
        'overflow-hidden',
        `rounded-[${borderRadiusV3[radius]}]`,
        variantClasses[variant],
        glow && 'shadow-lg hover:shadow-xl',
        'transform-gpu will-change-transform'
      )}>
        <div className="overflow-x-auto">
          <table 
            ref={ref}
            className={cn(
              'w-full',
              sizeClasses[size],
              animatingSort && 'transition-all duration-300'
            )}
          >
            {/* Header */}
            <thead>
              <tr className={cn(
                // Base header styles
                'border-b border-gray-200/50',
                
                // Glass morphism header
                glassMorphism && [
                  'bg-white/70 backdrop-blur-2xl saturate-[180%] border border-white/40 shadow-2xl',
                  'backdrop-blur-lg',
                  'bg-gradient-to-r from-white/60 to-white/40',
                ],
                
                !glassMorphism && 'bg-gray-50/80',
                
                headerClassName
              )}>
                {/* Select all column */}
                {selectable && multiSelect && (
                  <th className="w-12 text-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isIndeterminate
                      }}
                      onChange={handleSelectAll}
                      className={cn(
                        'w-5 h-5 rounded-lg',
                        'text-blue-600 focus:ring-blue-500/20',
                        'border-2 border-gray-300',
                        'transition-colors duration-200'
                      )}
                    />
                  </th>
                )}
                
                {/* Column headers */}
                {effectiveColumns
                  .filter(column => visibleColumns.has(column.id))
                  .map(column => (
                  <th
                    key={column.id}
                    className={cn(
                      'font-semibold text-gray-900',
                      `text-${column.align || 'left'}`,
                      column.sortable && 'cursor-pointer select-none',
                      'transition-colors duration-200',
                      column.sortable && 'hover:text-blue-600',
                      column.sticky && 'sticky left-0 z-10 bg-inherit'
                    )}
                    style={{
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                    }}
                    onClick={() => column.sortable && handleSort(column.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span>
                        {resolveText(column.headerEs, column.headerEn, column.header)}
                      </span>
                      {column.sortable && (
                        <div className="flex flex-col">
                          <ChevronUp 
                            size={12} 
                            className={cn(
                              'transition-colors duration-200',
                              sortColumn === column.id && sortDirection === 'asc'
                                ? 'text-blue-600' 
                                : 'text-gray-400'
                            )} 
                          />
                          <ChevronDown 
                            size={12} 
                            className={cn(
                              'transition-colors duration-200 -mt-0.5',
                              sortColumn === column.id && sortDirection === 'desc'
                                ? 'text-blue-600' 
                                : 'text-gray-400'
                            )} 
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
                
                {/* Actions column */}
                {rowActions.length > 0 && (
                  <th className="w-20 text-center">
                    <span className="text-gray-500">Acciones</span>
                  </th>
                )}
              </tr>
            </thead>
            
            {/* Body */}
            <tbody className={bodyClassName}>
              {data.map((item, index) => {
                const rowId = getRowId ? getRowId(item, index) : index
                const isSelected = selectedRows.has(rowId)
                const isHovered = hoveredRow === index
                const customRowClass = typeof rowClassName === 'function' 
                  ? rowClassName(item, index) 
                  : rowClassName
                
                return (
                  <tr
                    key={rowId}
                    className={cn(
                      // Base row styles
                      'transition-all duration-200',
                      'border-b border-gray-100/50 last:border-b-0',
                      
                      // Hover effects
                      hoverEffects && [
                        'hover:bg-gray-50/50',
                        glassMorphism && 'hover:bg-white/30',
                        'hover:shadow-sm',
                        'hover:scale-[1.01]',
                      ],
                      
                      // Striped rows
                      stripedRows && index % 2 === 1 && [
                        'bg-gray-25/30',
                        glassMorphism && 'bg-white/10',
                      ],
                      
                      // Selected state
                      isSelected && [
                        'bg-blue-50/80 border-blue-200/50',
                        glassMorphism && 'bg-blue-100/20',
                      ],
                      
                      // Clickable rows
                      (onRowClick || onRowDoubleClick) && 'cursor-pointer',
                      
                      customRowClass
                    )}
                    onClick={() => {
                      onRowClick?.(item, index)
                      if (selectable && !multiSelect) {
                        handleRowSelect(item, index)
                      }
                    }}
                    onDoubleClick={() => onRowDoubleClick?.(item, index)}
                    onMouseEnter={() => {
                      setHoveredRow(index)
                      onRowHover?.(item, index)
                    }}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    {/* Selection checkbox */}
                    {selectable && multiSelect && (
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleRowSelect(item, index)}
                          onClick={(e) => e.stopPropagation()}
                          className={cn(
                            'w-4 h-4 rounded',
                            'text-blue-600 focus:ring-blue-500/20',
                            'border-2 border-gray-300',
                            'transition-colors duration-200'
                          )}
                        />
                      </td>
                    )}
                    
                    {/* Data cells */}
                    {effectiveColumns
                      .filter(column => visibleColumns.has(column.id))
                      .map(column => {
                      const value = getCellValue(item, column)
                      const formattedValue = formatCellValue(value, column)
                      
                      return (
                        <td
                          key={column.id}
                          className={cn(
                            `text-${column.align || 'left'}`,
                            'text-gray-900',
                            column.sticky && 'sticky left-0 z-10 bg-inherit',
                            'transition-colors duration-200'
                          )}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                          }}
                        >
                          {column.cell ? column.cell(value, item, index) : (
                            <span className={cn(
                              column.isCurrency && 'font-mono',
                              column.isNumeric && 'font-mono',
                              column.isPercentage && 'font-mono'
                            )}>
                              {formattedValue}
                            </span>
                          )}
                        </td>
                      )
                    })}
                    
                    {/* Actions */}
                    {rowActions.length > 0 && (
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {rowActions
                            .filter(action => !action.hidden?.(item))
                            .map(action => (
                            <button
                              key={action.id}
                              onClick={(e) => {
                                e.stopPropagation()
                                action.onClick(item)
                              }}
                              disabled={action.disabled?.(item)}
                              className={cn(
                                'p-2 rounded-xl',
                                'transition-all duration-200',
                                'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                                
                                // Variant styles
                                action.variant === 'destructive' && [
                                  'text-red-600 hover:bg-red-50',
                                ],
                                action.variant === 'glass' && [
                                  'bg-white/30 backdrop-blur-md saturate-[150%] border border-white/20 shadow-lg',
                                  'text-gray-700 hover:bg-white/60',
                                ],
                                (!action.variant || action.variant === 'secondary') && [
                                  'text-gray-600 hover:bg-gray-100',
                                ],
                                action.variant === 'primary' && [
                                  'text-blue-600 hover:bg-blue-50',
                                ],
                                
                                // Disabled state
                                action.disabled?.(item) && 'opacity-50 cursor-not-allowed'
                              )}
                              title={resolveText(action.labelEs, action.labelEn, action.label)}
                            >
                              {action.icon}
                            </button>
                          ))}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
})

BirhausTableV3.displayName = 'BirhausTableV3'

/**
 * Hook for managing table state with v3 enhancements
 */
export const useBirhausTableV3 = <T,>(initialData: T[] = []) => {
  const [data, setData] = useState<T[]>(initialData)
  const [loading, setLoading] = useState(false)
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set())
  const [sortColumn, setSortColumn] = useState<string>()
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [filters, setFilters] = useState<Record<string, any>>({})
  const [searchValue, setSearchValue] = useState('')
  
  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column)
    setSortDirection(direction)
  }
  
  const handleRowSelect = (rowId: string | number) => {
    const newSelection = new Set([rowId])
    setSelectedRows(newSelection)
  }
  
  const handleRowsSelect = (rowIds: Set<string | number>) => {
    setSelectedRows(rowIds)
  }
  
  const handleFilter = (newFilters: Record<string, any>) => {
    setFilters(newFilters)
  }
  
  const handleSearch = (value: string) => {
    setSearchValue(value)
  }
  
  const clearSelection = () => {
    setSelectedRows(new Set())
  }
  
  const selectAll = (allRowIds: (string | number)[]) => {
    setSelectedRows(new Set(allRowIds))
  }
  
  return {
    data,
    setData,
    loading,
    setLoading,
    selectedRows,
    sortColumn,
    sortDirection,
    filters,
    searchValue,
    handleSort,
    handleRowSelect,
    handleRowsSelect,
    handleFilter,
    handleSearch,
    clearSelection,
    selectAll,
  }
}